import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import SubAccountLayout from '../../components/SubAccountLayout';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { TypeToConfirmDialog } from '../../components/TypeToConfirmDialog';
import { conversationMessageKey, mergeConversationMessages, smsStatusLabel } from '../../utils/conversationMessages';
import { SmsActivityRow } from '../../components/SmsActivityRow';
import { ChatDeliveryStatus } from '../../components/ChatDeliveryStatus';
import { useInboxScroll } from '../../hooks/useInboxScroll';
import {
  MessageSquare, Bot, User, Zap, AlertTriangle,
  CheckCircle, RefreshCw, Calendar, Trash2,
  Search, WifiOff, Wifi, ChevronDown, XCircle, Phone, Loader2, Globe, Users, Tag,
  Sparkles, Database, X, Hourglass,
  PhoneIncoming, PhoneOutgoing, Play, Pause, MoreVertical, Download, ArrowLeft,
  FileText
} from 'lucide-react';

const POLL_INTERVAL = 5000;  // 5s — conversations sidebar refresh
const ACTIVE_POLL_INTERVAL = 3000; // 3s when viewing a conversation

// Human-like delay countdown helpers
const delayRemaining = (c, now) => {
  if (!c?.reply_scheduled_at) return 0;
  const ms = new Date(c.reply_scheduled_at).getTime() - now;
  return ms > 0 ? Math.ceil(ms / 1000) : 0;
};
const fmtCountdown = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// Textback countdown — long-range "2d 4h 12m" formatting
const fmtLongCountdown = (s) => {
  if (s >= 86400) return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
  if (s >= 3600) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  if (s >= 60) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${s}s`;
};
// Derive the pending textback (if any) from the thread's function_result badges:
// latest successful schedule_textback with a future scheduled_at, not yet
// superseded by a sent "AI - Textback" message.
const pendingTextbackFrom = (messages, now) => {
  let pending = null;
  for (const m of messages || []) {
    const meta = m.meta || {};
    if (m.role === 'system' && meta.type === 'function_result' && meta.function === 'schedule_textback' && meta.status === 'success' && meta.details?.scheduled_at) {
      pending = { scheduledAt: meta.details.scheduled_at, channel: meta.details.channel || 'SMS', badgeTs: m.timestamp };
    } else if (meta.source === 'ai_textback' && pending && m.timestamp && pending.badgeTs && new Date(m.timestamp) > new Date(pending.badgeTs)) {
      pending = null; // that textback already went out
    }
  }
  if (!pending) return null;
  const secs = Math.ceil((new Date(pending.scheduledAt).getTime() - now) / 1000);
  return secs > 0 ? { ...pending, secondsLeft: secs } : null;
};

const ConversationsPageContent = () => {
  const { subAccountId } = useParams();
  const { isDarkMode } = useTheme();
  const { api } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [anonVisitors, setAnonVisitors] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    try { return localStorage.getItem('vx_conv_tab') || 'leads'; } catch { return 'leads'; }
  });
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [processorStatus, setProcessorStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(() => {
    try { return localStorage.getItem('vx_ai_panel') !== 'closed'; } catch { return true; }
  });
  const { threadRef, contentRef, onThreadScroll } = useInboxScroll(selectedSession?.session_id, messages);
  const pollRef = useRef(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const msgPollRef = useRef(null);
  const prevMsgCountRef = useRef(0);
  const dismissedRef = useRef(new Set());
  const selectedSessionRef = useRef(null);
  const messageRequestRef = useRef(0);
  const messageInFlightRef = useRef(null);

  const cardBg = isDarkMode ? 'bg-[#0c0d12]/80' : 'bg-white/80';
  const cardBorder = isDarkMode ? 'border-white/[0.06]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const inputBg = isDarkMode ? 'bg-white/[0.04]' : 'bg-gray-50';

  const fetchConversations = useCallback(async (isInitial = false) => {
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/conversations`);
      // New payload: { conversations: [...known leads merged...], known_leads: [...], anonymous_visitors: [...] }
      // Older deployments only return `conversations`. Stay backward-compatible.
      const known = res.data?.known_leads || res.data?.conversations || [];
      const anon = res.data?.anonymous_visitors || [];
      const filterDismissed = (arr) => arr.filter(c => !dismissedRef.current.has(c.session_id));
      setConversations(filterDismissed(known));
      setAnonVisitors(filterDismissed(anon));

      // Cross-sync is_processing + delay timer to selected session (search BOTH lists)
      const sel = selectedSessionRef.current;
      if (sel) {
        const all = [...known, ...anon];
        const updatedConvo = all.find(c => c.session_id === sel.session_id);
        if (updatedConvo && (updatedConvo.is_processing !== sel.is_processing || updatedConvo.reply_scheduled_at !== sel.reply_scheduled_at)) {
          setSelectedSession(prev => prev ? { ...prev, is_processing: updatedConvo.is_processing, reply_scheduled_at: updatedConvo.reply_scheduled_at } : prev);
        }
      }
    } catch (e) {
      // Never clear conversations on error — keep showing what we have
      console.error('Conversations fetch error:', e?.message);
    }
  }, [subAccountId, api]);

  const fetchProcessorStatus = useCallback(async () => {
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/chat-processor/status`);
      setProcessorStatus(res.data);
    } catch { setProcessorStatus(null); }
  }, [subAccountId, api]);

  const mergeMessages = mergeConversationMessages;

  const fetchMessages = useCallback(async (sessionId, silent = false) => {
    if (!sessionId) return;
    if (silent && messageInFlightRef.current?.sessionId === sessionId) return;
    const requestNumber = ++messageRequestRef.current;
    messageInFlightRef.current = { sessionId, requestNumber };
    if (!silent) setMessagesLoading(true);
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/conversations/${sessionId}/messages`);
      if (requestNumber !== messageRequestRef.current || (selectedSessionRef.current && selectedSessionRef.current.session_id !== sessionId)) return;
      const newMsgs = res.data?.messages || [];
      // Always merge with existing messages to prevent any message from disappearing
      if (silent) {
        setMessages(prev => mergeMessages(prev, newMsgs));
      } else {
        // On initial load: replace messages entirely (don't merge with stale conversation's messages)
        setMessages(mergeMessages([], newMsgs));
      }
      const sessionData = res.data;
      setSelectedSession(sessionData);
      selectedSessionRef.current = sessionData;
      // Auto-scroll only when new messages appear
      if (newMsgs.length !== prevMsgCountRef.current) {
        prevMsgCountRef.current = newMsgs.length;
      }
    } catch (e) {
      // On failure, NEVER clear messages — keep showing whatever we have
      if (requestNumber !== messageRequestRef.current) return;
      console.error('Messages fetch error:', e?.message);
      // If this was a non-silent initial load and we got nothing, retry once after 2s
      if (!silent) {
        setTimeout(async () => {
          if (requestNumber !== messageRequestRef.current || selectedSessionRef.current?.session_id !== sessionId) return;
          try {
            const retry = await api.get(`/api/sub-accounts/${subAccountId}/conversations/${sessionId}/messages`);
            const retryMsgs = retry.data?.messages || [];
            if (requestNumber !== messageRequestRef.current || selectedSessionRef.current?.session_id !== sessionId) return;
            if (retryMsgs.length > 0) {
              setMessages(mergeMessages([], retryMsgs));
              setSelectedSession(retry.data);
              selectedSessionRef.current = retry.data;
              prevMsgCountRef.current = retryMsgs.length;
            }
          } catch { /* silent retry failure */ }
        }, 2000);
      }
    } finally {
      if (messageInFlightRef.current?.requestNumber === requestNumber) messageInFlightRef.current = null;
      if (requestNumber === messageRequestRef.current) setMessagesLoading(false);
    }
  }, [subAccountId, api, mergeMessages]);

  const [deletingConv, setDeletingConv] = useState(false);

  const deleteConversation = async (sessionId) => {
    setDeletingConv(true);
    try {
      const resp = await api.delete(`/api/sub-accounts/${subAccountId}/conversations/${sessionId}`);
      dismissedRef.current.add(sessionId);
      setConversations(prev => prev.filter(c => c.session_id !== sessionId));
      setAnonVisitors(prev => prev.filter(c => c.session_id !== sessionId));
      if (selectedSession?.session_id === sessionId) {
        setSelectedSession(null);
        setMessages([]);
      }
      setDeleteConfirm(null);
      const n = resp?.data?.deleted_chat_sessions ?? 1;
      toast.success(n > 1 ? `Cleared ${n} chat sessions for this contact` : 'Conversation deleted');
    } catch (e) {
      console.error('Delete failed:', e);
      toast.error('Delete failed');
    } finally {
      setDeletingConv(false);
    }
  };

  // Init + background poll — SINGLE reliable loop for conversations + auto-process
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      setLoading(true);
      await Promise.all([fetchConversations(true), fetchProcessorStatus()]);
      setLoading(false);
    };
    init();

    // One poll loop: refresh conversations, check for unprocessed messages, trigger AI if needed
    const poll = async () => {
      if (!mounted) return;
      try {
        // Always refresh the conversation list
        await fetchConversations(false);

        // Reading the inbox must never generate or resend customer messages.
      } catch {}
      // Also refresh processor status periodically
      fetchProcessorStatus().catch(() => {});
    };

    pollRef.current = setInterval(poll, POLL_INTERVAL);
    return () => { mounted = false; clearInterval(pollRef.current); };
  }, [fetchConversations, fetchProcessorStatus, subAccountId, api]);

  // WebSocket: real-time conversation updates
  const wsRef = useRef(null);
  const wsReconnectRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !subAccountId) return;

    let alive = true;
    const connectWS = () => {
      if (!alive) return;
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${proto}//${window.location.host}/api/sub-accounts/${subAccountId}/conversations/ws?token=${token}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WS] Connected');
        // Send ping every 25s to keep alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 25000);
        ws._pingInterval = pingInterval;
      };

      ws.onmessage = (evt) => {
        try {
          const event = JSON.parse(evt.data);
          if (event.type === 'pong') return;

          if (event.type === 'new_message') {
            // If this is the selected session, append the message in real-time
            const sel = selectedSessionRef.current;
            if (sel && sel.session_id === event.session_id && event.message) {
              setMessages(prev => {
                const updated = mergeConversationMessages(prev, [event.message]);
                prevMsgCountRef.current = updated.length;
                return updated;
              });
            }
            // Update conversation list preview — or trigger refresh if it's a new session.
            // The session may live in either tab (known leads OR anon web visitors), so update both.
            if (event.message) {
              const updateList = (prev) => {
                const exists = prev.some(c => c.session_id === event.session_id);
                if (!exists) return prev;
                return prev.map(c =>
                  c.session_id === event.session_id
                    ? { ...c, last_message: (event.message.text || '').slice(0, 80), last_message_role: event.message.role, last_message_at: event.message.timestamp, message_count: (c.message_count || 0) + 1 }
                    : c
                );
              };
              let foundInKnown = false;
              setConversations(prev => {
                foundInKnown = prev.some(c => c.session_id === event.session_id);
                return updateList(prev);
              });
              setAnonVisitors(prev => updateList(prev));
              // If neither list has the session yet, refresh from server to surface it.
              if (!foundInKnown && !anonVisitors.some(c => c.session_id === event.session_id)) {
                fetchConversations(false);
              }
            }
          }

          if (event.type === 'message_delivery') {
            setMessages(previous => previous.map(message => message.meta?.outbound_attempt_id === event.outbound_attempt_id
              ? (['delivered', 'read', 'failed'].includes(message.meta.delivery_status) && ['sending', 'accepted', 'sent'].includes(event.status) ? message : { ...message, message_id: event.ghl_message_id || message.message_id,
                  meta: { ...message.meta, delivery_status: event.status, delivery_reason: event.reason || '',
                    ghl_message_id: event.ghl_message_id || message.meta.ghl_message_id } })
              : message));
            fetchConversations(false);
          }

          if (event.type === 'trigger_outbound_call_update') {
            // iter146: Twilio status callback closed the loop on a chat-triggered
            // outbound call — patch the PREPARING badge to its terminal state
            // (success / warning / failed) in-place. No reload needed.
            // NOTE: match on call_id ONLY (not session_id) — the open thread
            // merges sibling sessions (widget + SMS), so the badge may live in
            // a different session_id than the selected one.
            if (event.call_id) {
              setMessages(prev => prev.map(m => {
                if (m.meta?.function === 'trigger_outbound_call'
                    && m.meta?.details?.call_id === event.call_id) {
                  return {
                    ...m,
                    meta: {
                      ...m.meta,
                      status: event.status,
                      reason: event.reason || m.meta.reason,
                      details: { ...(m.meta.details || {}), ...(event.details || {}) },
                    },
                  };
                }
                return m;
              }));
            }
          }

          if (event.type === 'reply_delay') {
            // Human-like delay scheduled (or reset by a new inbound text)
            const patchDelay = (prev) => prev.map(c =>
              c.session_id === event.session_id ? { ...c, reply_scheduled_at: event.reply_scheduled_at, reply_delay_seconds: event.delay_seconds } : c
            );
            setConversations(patchDelay);
            setAnonVisitors(patchDelay);
            const selD = selectedSessionRef.current;
            if (selD && selD.session_id === event.session_id) {
              const updatedD = { ...selD, reply_scheduled_at: event.reply_scheduled_at, reply_delay_seconds: event.delay_seconds };
              setSelectedSession(prev => prev ? { ...prev, reply_scheduled_at: event.reply_scheduled_at, reply_delay_seconds: event.delay_seconds } : prev);
              selectedSessionRef.current = updatedD;
            }
          }

          if (event.type === 'processing') {
            // Update is_processing in BOTH lists (session may live in either tab)
            const updateProc = (prev) => prev.map(c =>
              c.session_id === event.session_id ? { ...c, is_processing: event.is_processing, processing_since: event.is_processing ? new Date().toISOString() : null } : c
            );
            setConversations(updateProc);
            setAnonVisitors(updateProc);
            // Update selected session
            const sel = selectedSessionRef.current;
            if (sel && sel.session_id === event.session_id) {
              const updated = { ...sel, is_processing: event.is_processing, processing_since: event.is_processing ? new Date().toISOString() : null };
              setSelectedSession(prev => prev ? { ...prev, is_processing: event.is_processing, processing_since: event.is_processing ? new Date().toISOString() : null } : prev);
              selectedSessionRef.current = updated;
              // Auto-scroll when AI starts thinking
              if (event.is_processing) {
              }
            }
          }
        } catch (e) {
          console.warn('[WS] Parse error:', e);
        }
      };

      ws.onclose = () => {
        clearInterval(ws._pingInterval);
        console.log('[WS] Disconnected');
        // Reconnect after 3s
        if (alive) wsReconnectRef.current = setTimeout(connectWS, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connectWS();

    return () => {
      alive = false;
      clearTimeout(wsReconnectRef.current);
      if (wsRef.current) {
        clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
    };
  }, [subAccountId]);

  // Message poll for selected conversation
  useEffect(() => {
    if (msgPollRef.current) clearInterval(msgPollRef.current);
    if (!selectedSession?.session_id) return;
    msgPollRef.current = setInterval(() => fetchMessages(selectedSession.session_id, true), ACTIVE_POLL_INTERVAL);
    return () => clearInterval(msgPollRef.current);
  }, [selectedSession?.session_id, fetchMessages]);

  // 1s tick that drives the human-like delay countdown chips + textback timers
  useEffect(() => {
    const anyDelay = [...conversations, ...anonVisitors, selectedSession || {}].some(
      c => c?.reply_scheduled_at && new Date(c.reply_scheduled_at).getTime() > Date.now()
    );
    const anyTextback = !!pendingTextbackFrom(messages, Date.now());
    if (!anyDelay && !anyTextback) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [conversations, anonVisitors, selectedSession, messages]);

  const selectConversation = (sessionId) => {
    prevMsgCountRef.current = 0;
    // Don't clear messages — let them persist until new ones load to avoid flash of empty state
    setMessagesLoading(true);
    // Immediately set selected from conversation list data for instant feedback
    const convo = [...conversations, ...anonVisitors].find(c => c.session_id === sessionId);
    if (convo) {
      const partial = { session_id: sessionId, contact_name: convo.contact_name, contact_phone: convo.contact_phone, agent_name: convo.agent_name, is_processing: convo.is_processing, reply_scheduled_at: convo.reply_scheduled_at, last_ai_skip: convo.last_ai_skip };
      setSelectedSession(partial);
      selectedSessionRef.current = partial;
    }
    fetchMessages(sessionId);
  };

  // Persist tab choice across reloads — operators tend to live in one tab.
  const switchTab = (tab) => {
    setActiveTab(tab);
    try { localStorage.setItem('vx_conv_tab', tab); } catch { /* ignore */ }
  };

  const toggleAiPanel = () => {
    setAiPanelOpen(prev => {
      try { localStorage.setItem('vx_ai_panel', prev ? 'closed' : 'open'); } catch { /* ignore */ }
      return !prev;
    });
  };

  const activeList = activeTab === 'web' ? anonVisitors : conversations;
  const pendingTextback = useMemo(() => pendingTextbackFrom(messages, nowTick), [messages, nowTick]);
  const filtered = activeList.filter(c =>
    !searchQuery || c.contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.contact_phone?.includes(searchQuery)
  );

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 60000) return 'Just now';
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
    if (diffMs < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Only display actual conversation messages (customer, agent, automation), plus function results
  const displayMessages = messages.filter(msg => {
    const role = msg.role;
    const meta = msg.meta || {};
    // Always show customer and agent messages
    if (role === 'customer' || role === 'agent') return true;
    // Show AI voice calls (inbound + outbound) merged into the thread
    if (role === 'call' && meta.type === 'voice_call') return true;
    // Show LeadConnector automation messages
    if (role === 'automation' || msg.source === 'ghl_automation') return true;
    // Show function results (consolidated: one per function)
    if (role === 'system' && meta.type === 'function_result') return true;
    // Show errors
    if (role === 'system' && meta.type === 'error') return true;
    // Show session boundary dividers (between stitched sessions)
    if (role === 'system' && meta.type === 'session_divider') return true;
    // Hide everything else (internal actions, raw function_call triggers)
    return false;
  });

  // Derive "AI is thinking" from both server flag and client-side heuristic
  const isProcessing = (() => {
    // Server-side flag (set by backend when AI starts processing)
    if (selectedSession?.is_processing) {
      const since = selectedSession?.processing_since;
      if (since) {
        const elapsed = Date.now() - new Date(since).getTime();
        if (elapsed > 45000) return false; // Stale — processing took too long (45s timeout)
      }
      // If no processing_since, still show indicator (trust the is_processing flag for up to 30s)
      // Also check: if the last message is already from the AI, processing is done
      if (displayMessages.length > 0) {
        const lastMsg = displayMessages[displayMessages.length - 1];
        if (lastMsg.role === 'agent' || lastMsg.role === 'automation') return false;
      }
      return true;
    }
    return false;
  })();

  const statusColor = processorStatus?.status === 'active' ? 'text-emerald-400' : processorStatus?.status === 'error' ? 'text-amber-400' : 'text-gray-500';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[70vh]">
        <Loader2 className="animate-spin text-teal-400" size={20} />
        <span className={`${textSecondary} ml-3 text-sm`}>Loading conversations...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col h-full min-h-0 overflow-hidden p-3" data-testid="conversations-page">
      {processorStatus?.status === 'error' && processorStatus?.ghl_error && (
        <div className="mb-3 px-4 py-2.5 rounded-xl bg-amber-400/10 border border-amber-400/20 flex items-center gap-2" data-testid="processor-error-banner">
          <AlertTriangle className="text-amber-400 shrink-0" size={14} />
          <span className="text-xs text-amber-400 flex-1">LeadConnector connection has failed. Your credentials may have expired.</span>
          <a href={`/sub-accounts/${subAccountId}/settings`} className="text-xs text-amber-300 underline hover:text-amber-200 shrink-0 font-medium">Reconnect in Settings</a>
        </div>
      )}

      <div className="flex flex-1 gap-3 min-h-0">
        {/* LEFT: Contact list */}
        <div className={`${selectedSession ? 'hidden md:flex' : 'flex'} w-full md:w-[300px] shrink-0 ${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} flex-col min-h-0`}>
          <div className={`p-3.5 border-b ${cardBorder} shrink-0`}>
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-2">
                <MessageSquare className="text-teal-400" size={14} />
                <h2 className={`text-sm font-semibold ${textPrimary}`}>Inbox</h2>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-md ${inputBg} ${textSecondary} font-medium`}>{filtered.length}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md ${isDarkMode ? 'bg-white/[0.04]' : 'bg-gray-100'}`}>
                  {processorStatus?.status === 'active' ? <Wifi size={9} className={statusColor} /> : <WifiOff size={9} className={statusColor} />}
                  <span className={`text-[9px] font-medium ${statusColor}`}>
                    {processorStatus?.status === 'active' ? 'Live' : processorStatus?.status === 'error' ? 'Error' : 'Idle'}
                  </span>
                </div>
                <button
                  onClick={async () => {
                    setScanning(true);
                    try {
                      await Promise.all([fetchConversations(), fetchProcessorStatus()]);
                      if (selectedSessionRef.current?.session_id) await fetchMessages(selectedSessionRef.current.session_id, true);
                    } catch (e) {
                      toast.error('Scan failed — check LeadConnector connection');
                    }
                    setScanning(false);
                  }}
                  disabled={scanning}
                  className={`p-1 rounded-md ${isDarkMode ? 'bg-white/[0.04] hover:bg-white/[0.08]' : 'bg-gray-100 hover:bg-gray-200'} transition-colors ${scanning ? 'opacity-50' : ''}`}
                  title="Refresh inbox"
                  data-testid="force-scan-btn"
                >
                  <RefreshCw size={10} className={`text-teal-400 ${scanning ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            {/* Tabs — split known leads (merged by phone) vs anonymous web visitors */}
            <div className={`flex items-center gap-1 mb-2 p-0.5 rounded-lg ${isDarkMode ? 'bg-white/[0.04]' : 'bg-gray-100'}`} data-testid="conversation-tabs">
              <button
                type="button"
                onClick={() => switchTab('leads')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                  activeTab === 'leads'
                    ? (isDarkMode ? 'bg-teal-400/15 text-teal-300 shadow-sm' : 'bg-white text-teal-600 shadow-sm')
                    : `${textSecondary} hover:${textPrimary}`
                }`}
                data-testid="tab-known-leads"
              >
                <Users size={11} />
                <span>Known Leads</span>
                <span className={`text-[9px] px-1 rounded ${activeTab === 'leads' ? (isDarkMode ? 'bg-teal-400/20 text-teal-200' : 'bg-teal-50 text-teal-700') : (isDarkMode ? 'bg-white/[0.06] text-gray-400' : 'bg-gray-200 text-gray-600')}`}>
                  {conversations.length}
                </span>
              </button>
              <button
                type="button"
                onClick={() => switchTab('web')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                  activeTab === 'web'
                    ? (isDarkMode ? 'bg-teal-400/15 text-teal-300 shadow-sm' : 'bg-white text-teal-600 shadow-sm')
                    : `${textSecondary} hover:${textPrimary}`
                }`}
                data-testid="tab-web-visitors"
              >
                <Globe size={11} />
                <span>Web Visitors</span>
                <span className={`text-[9px] px-1 rounded ${activeTab === 'web' ? (isDarkMode ? 'bg-teal-400/20 text-teal-200' : 'bg-teal-50 text-teal-700') : (isDarkMode ? 'bg-white/[0.06] text-gray-400' : 'bg-gray-200 text-gray-600')}`}>
                  {anonVisitors.length}
                </span>
              </button>
            </div>
            <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg ${inputBg} border ${cardBorder}`}>
              <Search size={12} className={textSecondary} />
              <input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full bg-transparent text-xs ${textPrimary} placeholder:text-gray-500 outline-none`} data-testid="conversation-search" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <MessageSquare className={textSecondary} size={24} />
                <p className={`text-xs ${textSecondary} mt-3 text-center`}>
                  {activeTab === 'web'
                    ? 'No anonymous web visitors yet'
                    : 'No known leads yet'}
                </p>
                <p className={`text-[10px] ${textSecondary} mt-1 text-center max-w-[220px]`}>
                  {activeTab === 'web'
                    ? 'Visitors who chat on your widget without sharing contact info will appear here.'
                    : 'SMS, voice, and identified widget conversations group together by phone number.'}
                </p>
                {activeTab !== 'web' && (
                  <button
                    onClick={async () => {
                      try {
                        await fetchConversations();
                      } catch {}
                    }}
                    className="mt-3 px-3 py-1.5 rounded-lg bg-teal-500/20 text-teal-400 text-[10px] font-medium hover:bg-teal-500/30 transition-colors"
                    data-testid="force-process-btn"
                  >
                    <RefreshCw size={10} className="inline mr-1" /> Refresh inbox
                  </button>
                )}
              </div>
            ) : (
              filtered.map((conv) => (
                <div key={conv.session_id} className="relative group">
                  <button onClick={() => selectConversation(conv.session_id)}
                    className={`w-full text-left px-3.5 py-3 border-b ${cardBorder} transition-all duration-150 ${
                      selectedSession?.session_id === conv.session_id
                        ? isDarkMode ? 'bg-teal-400/[0.06] border-l-2 border-l-teal-400' : 'bg-teal-50 border-l-2 border-l-teal-500'
                        : `hover:${isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50'} border-l-2 border-l-transparent`
                    }`} data-testid={`conversation-item-${conv.session_id}`}>
                    <div className="flex items-start gap-2.5">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${conv.is_anonymous ? (isDarkMode ? 'bg-violet-400/10' : 'bg-violet-50') : (isDarkMode ? 'bg-teal-400/10' : 'bg-teal-50')}`}>
                        {conv.is_anonymous
                          ? <Globe size={13} className="text-violet-400" />
                          : <span className="text-xs font-semibold text-teal-500">{(conv.contact_name || 'U')[0].toUpperCase()}</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className={`text-[13px] font-medium ${textPrimary} truncate`}>{conv.contact_name || 'Unknown'}</span>
                          <span className={`text-[10px] ${textSecondary} shrink-0 ml-2`}>{formatTime(conv.last_message_at)}</span>
                        </div>
                        <p className={`text-[11px] ${textSecondary} truncate mt-0.5`}>
                          {conv.last_message_role === 'agent' && <Bot size={9} className="inline mr-1 text-teal-400" />}
                          {conv.last_message || 'No messages'}
                        </p>
                        {/* Tag-Audit chip — shows which activation_tag matched
                            this contact to this agent on which channel. Lets
                            operators debug "why did THIS agent fire?" without
                            digging through server logs. */}
                        {conv.activation_tag && (
                          <div
                            className={`flex items-center gap-1 mt-1 text-[10px] ${textSecondary} truncate`}
                            title={`Matched by tag "${conv.activation_tag}" → ${conv.agent_name}${(Array.isArray(conv.channels) && conv.channels.length) ? ' (' + conv.channels.join(', ').toUpperCase() + ')' : ''}`}
                            data-testid={`tag-match-log-${conv.session_id}`}
                          >
                            <Tag size={9} className="text-emerald-400 shrink-0" />
                            <span className="truncate">
                              <span className="font-medium text-emerald-400">{conv.activation_tag}</span>
                              <span className="opacity-60"> → {conv.agent_name}</span>
                              {Array.isArray(conv.channels) && conv.channels.length > 0 && (
                                <span className="opacity-60 uppercase ml-1">· {conv.channels[0] === 'widget' ? 'Web' : conv.channels[0]}</span>
                              )}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {conv.ai_paused && (
                            <span className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-slate-400/15 text-slate-500 dark:text-slate-300 font-medium" title="The AI activation tag was removed from this contact. Add an agent tag to resume AI replies." data-testid={`ai-paused-badge-${conv.session_id}`}>
                              <Pause size={8} /> AI paused · tag removed
                            </span>
                          )}
                          {conv.is_processing && (
                            <span className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-amber-400/10 text-amber-400 font-medium">
                              <Loader2 size={8} className="animate-spin" /> Processing
                            </span>
                          )}
                          {/* Human-like delay countdown — reply scheduled, waiting to feel human */}
                          {!conv.is_processing && delayRemaining(conv, nowTick) > 0 && (
                            <span className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-sky-400/10 text-sky-400 font-medium" data-testid={`reply-delay-chip-${conv.session_id}`}>
                              <Hourglass size={8} /> Replying in {fmtCountdown(delayRemaining(conv, nowTick))}
                            </span>
                          )}
                          {/* "ai chat booked" pill — fires when AI agent books an appointment for this lead */}
                          {Array.isArray(conv.tags_applied) && conv.tags_applied.includes('ai chat booked') && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-md font-medium bg-violet-500/15 text-violet-300 border border-violet-400/30" title="LeadConnector tag applied: ai chat booked" data-testid="ghl-tag-pill-ai-chat-booked">
                              ai chat booked
                            </span>
                          )}
                          {/* Merged-session count badge — only when 2+ sessions for the same lead */}
                          {(conv.session_count || 0) > 1 && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-400/10 text-violet-400 font-medium" title="Merged sessions for this lead">
                              {conv.session_count} threads
                            </span>
                          )}
                          {/* Channel chips — only meaningful when multi-channel */}
                          {Array.isArray(conv.channels) && conv.channels.length > 1 && conv.channels.slice(0, 3).map((ch) => (
                            <span key={ch} className={`text-[9px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-white/[0.04] text-gray-400' : 'bg-gray-100 text-gray-600'} uppercase tracking-wide`}>
                              {ch === 'widget' ? 'Web' : ch}
                            </span>
                          ))}
                          <span className={`text-[9px] px-1.5 py-0.5 rounded ${inputBg} ${textSecondary}`}>{conv.message_count}</span>
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-teal-400/10 text-teal-400 truncate max-w-[100px]">{conv.agent_name}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                  {/* Delete button on hover */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(conv.session_id); }}
                    className="absolute top-2 right-2 p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity bg-red-500/10 hover:bg-red-500/20 text-red-400"
                    data-testid={`delete-conversation-${conv.session_id}`}
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* RIGHT: Message thread */}
        <div className={`${selectedSession ? 'flex' : 'hidden md:flex'} flex-1 min-w-0 ${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} flex-col min-h-0`}>
          {!selectedSession ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${isDarkMode ? 'bg-teal-400/10' : 'bg-teal-50'}`}>
                <MessageSquare className="text-teal-400" size={20} />
              </div>
              <p className={`text-sm font-medium ${textPrimary}`}>Select a conversation</p>
              <p className={`text-xs ${textSecondary} mt-1`}>Choose a contact to view the thread</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className={`px-4 py-2.5 border-b ${cardBorder} flex flex-wrap gap-2 items-center justify-between shrink-0`}>
                <button type="button" className={`md:hidden p-1 ${textSecondary}`} title="Back to conversations" aria-label="Back to conversations" data-testid="inbox-back-to-conversations" onClick={() => { messageRequestRef.current += 1; selectedSessionRef.current = null; setSelectedSession(null); setMessages([]); }}><ArrowLeft size={18} /></button>
                <div className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-teal-400/10' : 'bg-teal-50'}`}>
                    <span className="text-xs font-semibold text-teal-500">{(selectedSession.contact_name || 'U')[0].toUpperCase()}</span>
                  </div>
                  <div>
                    <p className={`text-[13px] font-semibold ${textPrimary}`}>{selectedSession.contact_name}</p>
                    <p className={`text-[10px] ${textSecondary}`}>{selectedSession.contact_phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {selectedSession.last_ai_skip?.reason && (
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-400/10 border border-amber-400/20"
                      title={selectedSession.last_ai_skip.detail || ''} data-testid="ai-skip-chip">
                      <AlertTriangle size={10} className="text-amber-400" />
                      <span className="text-[10px] text-amber-400 font-medium">
                        {{
                          no_credits: 'AI paused — balance depleted',
                          lock_timeout: 'A message was skipped — reply lock busy',
                          generation_failed: 'AI failed to respond — see error in thread',
                          agent_paused: 'AI paused — agent is paused',
                          agent_inactive: 'AI paused — agent is not active',
                          chat_disabled: 'AI paused — Chat Assistant disabled',
                        }[selectedSession.last_ai_skip.reason] || `AI skipped: ${selectedSession.last_ai_skip.reason}`}
                      </span>
                    </div>
                  )}
                  {conversations.some(c => c.session_id === selectedSession.session_id && c.ai_paused) && (
                    <ResumeAIButton api={api} subAccountId={subAccountId} sessionId={selectedSession.session_id}
                      onResumed={() => fetchConversations()} />
                  )}
                  <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-teal-400/10">
                    <Bot size={10} className="text-teal-400" />
                    <span className="text-[10px] text-teal-400 font-medium">{selectedSession.agent_name}</span>
                  </div>
                  <button onClick={toggleAiPanel}
                    className={`p-1.5 rounded-lg ${inputBg} border ${aiPanelOpen ? 'border-violet-400/30 bg-violet-400/10' : cardBorder} hover:border-violet-400/30 hover:bg-violet-400/10 transition-colors`}
                    title={aiPanelOpen ? 'Hide Captured by AI panel' : 'Show Captured by AI panel'}
                    data-testid="ai-activity-toggle">
                    <Sparkles size={12} className={aiPanelOpen ? 'text-violet-400' : textSecondary} />
                  </button>
                  <button onClick={() => setDeleteConfirm(selectedSession.session_id)}
                    className={`p-1.5 rounded-lg ${inputBg} border ${cardBorder} hover:border-red-400/30 hover:bg-red-400/10 transition-colors`}
                    data-testid="delete-conversation-header">
                    <Trash2 size={12} className={textSecondary} />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div ref={threadRef} onScroll={onThreadScroll} className="flex-1 overflow-y-auto overscroll-contain min-h-0 px-4 py-3" data-testid="messages-thread">
                <div ref={contentRef} className="space-y-2" data-testid="messages-thread-content">
                {messagesLoading && messages.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="animate-spin text-teal-400" size={16} />
                    <span className={`text-sm ${textSecondary} ml-2`}>Loading...</span>
                  </div>
                ) : displayMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <p className={`text-sm ${textSecondary}`}>No messages yet</p>
                  </div>
                ) : (
                  displayMessages.map((msg, i) => (
                    msg.role === 'call'
                      ? <VoiceCallCard key={conversationMessageKey(msg)} msg={msg} api={api} subAccountId={subAccountId} isDarkMode={isDarkMode} textSecondary={textSecondary} />
                      : <MessageBubble key={conversationMessageKey(msg)} msg={msg} isDarkMode={isDarkMode} textSecondary={textSecondary} inputBg={inputBg} />
                  ))
                )}

                {/* Processing indicator — AI is thinking */}
                {isProcessing && (
                  <div className="flex justify-end" data-testid="ai-processing-indicator">
                    <div className="max-w-[70%]">
                      <div className="flex items-center justify-end gap-1.5 mb-0.5">
                        <Bot size={10} className="text-teal-400" />
                        <span className="text-[10px] text-teal-400 font-semibold">AI</span>
                      </div>
                      <div className={`px-4 py-3 rounded-2xl rounded-tr-sm ${isDarkMode ? 'bg-teal-500/[0.12] border border-teal-400/20 shadow-lg shadow-teal-500/5' : 'bg-teal-50 border border-teal-300 shadow-md'}`}>
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1.5">
                            <span className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-teal-400' : 'bg-teal-500'} animate-bounce`} style={{animationDelay: '0ms'}} />
                            <span className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-teal-400' : 'bg-teal-500'} animate-bounce`} style={{animationDelay: '150ms'}} />
                            <span className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-teal-400' : 'bg-teal-500'} animate-bounce`} style={{animationDelay: '300ms'}} />
                          </div>
                          <span className={`text-xs font-medium ${isDarkMode ? 'text-teal-300' : 'text-teal-600'} ml-1`}>Thinking...</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Human-like delay countdown — reply scheduled, waiting to feel human */}
                {!isProcessing && delayRemaining(selectedSession, nowTick) > 0 && (
                  <div className="flex justify-end" data-testid="reply-delay-indicator">
                    <div className="max-w-[70%]">
                      <div className="flex items-center justify-end gap-1.5 mb-0.5">
                        <Bot size={10} className="text-sky-400" />
                        <span className="text-[10px] text-sky-400 font-semibold">AI</span>
                      </div>
                      <div className={`px-4 py-3 rounded-2xl rounded-tr-sm ${isDarkMode ? 'bg-sky-500/[0.10] border border-sky-400/20' : 'bg-sky-50 border border-sky-200'}`}>
                        <div className="flex items-center gap-2">
                          <Hourglass size={12} className={`${isDarkMode ? 'text-sky-300' : 'text-sky-600'} animate-pulse`} />
                          <span className={`text-xs font-medium ${isDarkMode ? 'text-sky-300' : 'text-sky-600'}`} data-testid="reply-delay-countdown">
                            Response delay — replying in {fmtCountdown(delayRemaining(selectedSession, nowTick))}
                          </span>
                        </div>
                        <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-sky-300/60' : 'text-sky-500/70'}`}>
                          Analyzing messages · a new text restarts the timer &amp; merges into one reply
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {/* Textback countdown — a scheduled AI text is waiting to fire */}
                {pendingTextback && (
                  <div className="flex justify-end" data-testid="textback-pending-indicator">
                    <div className="max-w-[70%]">
                      <div className="flex items-center justify-end gap-1.5 mb-0.5">
                        <Bot size={10} className="text-teal-400" />
                        <span className="text-[10px] text-teal-400 font-semibold">AI - Textback</span>
                      </div>
                      <div className={`px-4 py-3 rounded-2xl rounded-tr-sm ${isDarkMode ? 'bg-teal-500/[0.10] border border-teal-400/20' : 'bg-teal-50 border border-teal-200'}`}>
                        <div className="flex items-center gap-2">
                          <Hourglass size={12} className={`${isDarkMode ? 'text-teal-300' : 'text-teal-600'} animate-pulse`} />
                          <span className={`text-xs font-medium ${isDarkMode ? 'text-teal-300' : 'text-teal-600'}`} data-testid="textback-countdown">
                            Textback sending in {fmtLongCountdown(pendingTextback.secondsLeft)}
                          </span>
                        </div>
                        <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-teal-300/60' : 'text-teal-500/70'}`}>
                          Scheduled for {new Date(pendingTextback.scheduledAt).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · via {pendingTextback.channel}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* RIGHT: Captured by AI — live tool/function activity for the selected contact */}
        {selectedSession && aiPanelOpen && (
          <AIActivityPanel
            messages={messages}
            isDarkMode={isDarkMode}
            cardBg={cardBg}
            cardBorder={cardBorder}
            textPrimary={textPrimary}
            textSecondary={textSecondary}
            onClose={toggleAiPanel}
          />
        )}
      </div>

      {/* Delete confirmation — typed "DELETE" gate (prevents accidents) */}
      <TypeToConfirmDialog
        open={!!deleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConversation(deleteConfirm)}
        title="Delete conversation?"
        description="This wipes every chat session for this contact in this sub-account. Orb calls and inbound/outbound voice calls are not affected. This cannot be undone."
        destructiveLabel="Delete conversation"
        isDarkMode={isDarkMode}
        busy={deletingConv}
        testIdPrefix="delete-conversation"
      />
    </div>
  );
};


// ── AI Voice Call card — rendered inline in the inbox thread ─────────
const PLAYBACK_SPEEDS = [0.75, 1, 1.25, 1.5, 2];
const fmtCallDur = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, '0')}`;

const VoiceCallCard = ({ msg, api, subAccountId, isDarkMode, textSecondary }) => {
  const meta = msg.meta || {};
  const inbound = (meta.direction || '') === 'inbound';
  const answered = !!meta.answered;
  const transcript = Array.isArray(meta.transcript) ? meta.transcript : [];
  const [showTranscript, setShowTranscript] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [curTime, setCurTime] = useState(0);
  const [dur, setDur] = useState(0);
  const [speed, setSpeed] = useState(1);
  const audioRef = useRef(null);

  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const ts = msg.timestamp ? new Date(msg.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
  const DirIcon = inbound ? PhoneIncoming : PhoneOutgoing;

  const loadAudio = async () => {
    const a = audioRef.current;
    if (!a) return false;
    if (a.src) return true;
    setLoadingAudio(true);
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/calls/${meta.call_id}/recording/stream`, { responseType: 'blob' });
      a.src = URL.createObjectURL(res.data);
      return true;
    } catch {
      toast.error('Could not load call recording');
      return false;
    } finally {
      setLoadingAudio(false);
    }
  };

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); return; }
    const ok = await loadAudio();
    if (!ok) return;
    a.playbackRate = speed;
    a.play().then(() => setPlaying(true)).catch(() => {});
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * dur;
    setCurTime(a.currentTime);
  };

  const setPlaybackSpeed = (sp) => {
    setSpeed(sp);
    if (audioRef.current) audioRef.current.playbackRate = sp;
    setMenuOpen(false);
  };

  const downloadRecording = async () => {
    setMenuOpen(false);
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/calls/${meta.call_id}/recording/stream`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const aEl = document.createElement('a');
      aEl.href = url;
      aEl.download = `call-${meta.call_id || 'recording'}.mp3`;
      document.body.appendChild(aEl);
      aEl.click();
      aEl.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Recording download started');
    } catch {
      toast.error('Download failed');
    }
  };

  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [summaryOverflow, setSummaryOverflow] = useState(false);
  const summaryRef = useRef(null);
  useEffect(() => {
    const el = summaryRef.current;
    if (el) setSummaryOverflow(el.scrollHeight > el.clientHeight + 1);
  }, [msg.text]);

  return (
    <div className={`flex ${inbound ? 'justify-start' : 'justify-end'}`} data-testid="voice-call-card">
      <div className={`w-full min-w-0 max-w-full md:max-w-[78%] rounded-2xl border p-3.5 ${isDarkMode ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
        {/* Header */}
        <div className="flex items-start gap-2.5">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${answered ? 'bg-teal-400/10' : 'bg-rose-400/10'}`}>
            <DirIcon size={15} className={answered ? 'text-teal-400' : 'text-rose-400'} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={`text-[12px] font-semibold ${textPrimary}`}>{meta.agent_name || 'AI Agent'}</span>
              <span className={`text-[10px] ${textSecondary}`}>
                {inbound ? 'Inbound' : 'Outbound'} voice call{meta.duration > 0 ? ` · ${fmtCallDur(meta.duration)} min` : ''}
              </span>
              <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${answered ? 'bg-emerald-400/10 text-emerald-400' : 'bg-rose-400/10 text-rose-400'}`} data-testid="voice-call-status-badge">
                {answered ? 'Answered' : 'Not Answered'}
              </span>
              {meta.booked && (
                <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-400/10 text-emerald-400" data-testid="voice-call-booked-chip">
                  AI Voice Booked
                </span>
              )}
              <span className={`text-[9px] ${textSecondary}`}>{ts}</span>
            </div>
            {/* Summary — clamped to 2 lines; See more only when truly overflowing */}
            <p ref={summaryRef} className={`text-[12px] mt-1 leading-relaxed whitespace-pre-wrap ${summaryExpanded ? '' : 'line-clamp-2'} ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`} data-testid="voice-call-summary">
              {msg.text}
            </p>
            {(summaryOverflow || summaryExpanded) && (
              <button
                onClick={() => setSummaryExpanded(v => !v)}
                className="text-[11px] font-medium text-teal-400 hover:text-teal-300 mt-0.5 transition-colors"
                data-testid="voice-call-summary-toggle"
              >
                {summaryExpanded ? 'See less' : 'See more...'}
              </button>
            )}
          </div>
          {transcript.length > 0 && (
            <button
              onClick={() => setShowTranscript(v => !v)}
              className="text-[10px] font-medium text-teal-400 hover:text-teal-300 shrink-0 flex items-center gap-0.5 transition-colors"
              data-testid="voice-call-transcript-toggle"
            >
              {showTranscript ? 'Hide transcript' : 'View transcript'}
              <ChevronDown size={10} className={`transition-transform ${showTranscript ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>

        {/* Audio player */}
        {meta.has_recording && (
          <div className={`mt-2.5 flex items-center gap-2.5 px-3 py-2 rounded-xl border ${isDarkMode ? 'bg-white/[0.03] border-white/10' : 'bg-gray-50 border-gray-200'}`} data-testid="voice-call-audio-player">
            <button
              onClick={togglePlay}
              disabled={loadingAudio}
              className="w-8 h-8 rounded-full bg-teal-500 hover:bg-teal-400 text-white flex items-center justify-center shrink-0 transition-colors disabled:opacity-60"
              data-testid="voice-call-play-btn"
            >
              {loadingAudio ? <Loader2 size={13} className="animate-spin" /> : playing ? <Pause size={13} /> : <Play size={13} className="ml-0.5" />}
            </button>
            <span className={`text-[10px] font-mono ${textSecondary} shrink-0`}>
              {fmtCallDur(curTime)} / {fmtCallDur(dur || meta.duration)}
            </span>
            <div
              onClick={seek}
              className={`flex-1 h-1.5 rounded-full cursor-pointer ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`}
              data-testid="voice-call-seek-bar"
            >
              <div
                className="h-full rounded-full bg-teal-400 transition-[width]"
                style={{ width: `${dur ? Math.min(100, (curTime / dur) * 100) : 0}%` }}
              />
            </div>
            {speed !== 1 && <span className="text-[9px] font-semibold text-teal-400 shrink-0">{speed}×</span>}
            <div className="relative shrink-0">
              <button
                onClick={() => setMenuOpen(v => !v)}
                className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-200'} ${textSecondary}`}
                data-testid="voice-call-menu-btn"
              >
                <MoreVertical size={13} />
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                  <div className={`absolute right-0 top-8 z-50 w-44 rounded-xl border shadow-2xl overflow-hidden ${isDarkMode ? 'bg-[#101117] border-white/10' : 'bg-white border-gray-200'}`} data-testid="voice-call-menu">
                    <button
                      onClick={downloadRecording}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-[11px] font-medium ${textPrimary} ${isDarkMode ? 'hover:bg-white/[0.06]' : 'hover:bg-gray-50'} transition-colors`}
                      data-testid="voice-call-download-btn"
                    >
                      <Download size={12} className="text-teal-400" /> Download MP3
                    </button>
                    <div className={`px-3 pt-2 pb-1 text-[9px] uppercase tracking-wide font-semibold ${textSecondary} border-t ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
                      Playback speed
                    </div>
                    {PLAYBACK_SPEEDS.map(sp => (
                      <button
                        key={sp}
                        onClick={() => setPlaybackSpeed(sp)}
                        className={`w-full flex items-center justify-between px-3 py-1.5 text-[11px] ${speed === sp ? 'text-teal-400 font-semibold' : textPrimary} ${isDarkMode ? 'hover:bg-white/[0.06]' : 'hover:bg-gray-50'} transition-colors`}
                        data-testid={`voice-call-speed-${String(sp).replace('.', '-')}`}
                      >
                        {sp}× {speed === sp && <CheckCircle size={11} />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Transcript dropdown */}
        {showTranscript && transcript.length > 0 && (
          <div className={`mt-2.5 rounded-xl border max-h-64 overflow-y-auto p-3 space-y-2 ${isDarkMode ? 'bg-black/20 border-white/10' : 'bg-gray-50 border-gray-200'}`} data-testid="voice-call-transcript">
            {transcript.map((t, j) => {
              const isAI = (t.role || '') === 'agent' || (t.role || '') === 'assistant' || (t.role || '') === 'ai';
              return (
                <div key={j} className="flex gap-2">
                  <span className={`text-[9px] font-bold uppercase tracking-wide shrink-0 w-14 ${isAI ? 'text-cyan-400' : 'text-teal-400'}`}>
                    {isAI ? 'AI' : 'Customer'}
                  </span>
                  <p className={`text-[11px] leading-relaxed flex-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t.text || t.message || ''}</p>
                </div>
              );
            })}
          </div>
        )}

        <audio
          ref={audioRef}
          onTimeUpdate={() => setCurTime(audioRef.current?.currentTime || 0)}
          onLoadedMetadata={() => setDur(audioRef.current?.duration || 0)}
          onEnded={() => setPlaying(false)}
          className="hidden"
        />
      </div>
    </div>
  );
};


const MessageBubble = ({ msg, isDarkMode, textSecondary, inputBg }) => {
  const [expanded, setExpanded] = useState(false);
  const role = msg.role;
  const meta = msg.meta || {};
  const isAutomation = role === 'automation' || msg.source === 'ghl_automation';
  const isFollowUp = msg.source === 'follow_up_ai';
  const isAgent = !isFollowUp && (role === 'agent' || msg.source === 'ai');
  const isError = meta.type === 'error';
  const isFunctionResult = meta.type === 'function_result';
  const isSessionDivider = meta.type === 'session_divider';

  const ts = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

  // Session-boundary divider — separates stitched conversations (e.g.
  // multiple widget chats from the same contact across days, or an
  // SMS/widget channel switch). One subtle line + a centered label.
  if (isSessionDivider) {
    const label = meta.channel_label || meta.channel || 'chat';
    let when = '';
    if (meta.started_at) {
      try {
        const d = new Date(meta.started_at);
        when = d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      } catch { /* ignore */ }
    }
    return (
      <div className="flex items-center gap-2 my-3" data-testid="message-session-divider">
        <div className={`flex-1 h-px ${isDarkMode ? 'bg-white/[0.08]' : 'bg-gray-200'}`} />
        <div className={`text-[10px] uppercase tracking-wider font-medium ${textSecondary} px-2`}>
          New {label} conversation{when ? ` • ${when}` : ''}
        </div>
        <div className={`flex-1 h-px ${isDarkMode ? 'bg-white/[0.08]' : 'bg-gray-200'}`} />
      </div>
    );
  }

  // Error messages
  if (isError) {
    return (
      <div className="flex justify-center my-1" data-testid="message-error">
        <button onClick={() => setExpanded(!expanded)} className="inline-flex flex-col items-center max-w-md">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-400/10 border border-red-400/10 hover:bg-red-400/15 transition-colors cursor-pointer">
            <XCircle size={10} className="text-red-400" />
            <span className="text-[10px] text-red-400 font-medium">{meta.function || 'Error'}</span>
            <ChevronDown size={9} className={`text-red-400/60 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </div>
          <AnimatePresence>
            {expanded && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                className="mt-1 px-3 py-2 rounded-lg bg-red-400/[0.05] border border-red-400/10 text-left">
                <p className="text-[10px] text-red-300/80">{msg.text}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </button>
      </div>
    );
  }

  // Consolidated function result (single row per function)
  if (isFunctionResult && meta.details?.sms_status) return <SmsActivityRow message={msg} isDarkMode={isDarkMode} />;
  if (isFunctionResult) {
    const funcName = meta.function || 'unknown';
    const isSuccess = meta.status === 'success' || meta.status === 'completed';
    // Treat ALL in-flight call states as "preparing" — they will resolve later.
    // Without this, legacy chat rows that wrote status='queued' or any
    // future Twilio status string ('initiated', 'ringing', 'in-progress')
    // would fall through to the FAILED red badge — falsely showing users
    // that a successful call failed. See iter145.
    const isPreparing = meta.status === 'preparing'
      || meta.status === 'slots_found'
      || meta.status === 'queued'
      || meta.status === 'initiated'
      || meta.status === 'ringing'
      || meta.status === 'in-progress'
      || meta.status === 'warming';
    const isWarning = meta.status === 'warning';
    const icon = funcName === 'check_availability' ? Calendar
      : funcName === 'book_appointment' ? CheckCircle
      : funcName === 'trigger_outbound_call' ? Phone
      : funcName === 'schedule_textback' ? MessageSquare
      : Zap;
    const Icon = icon;
    const details = meta.details || {};
    const statusLabel = details.sms_status ? smsStatusLabel(details.sms_status) : isPreparing ? 'PREPARING' : isSuccess ? 'SUCCESS' : isWarning ? 'WARNING' : 'FAILED';
    const statusColor = isPreparing ? 'text-blue-400' : isSuccess ? 'text-emerald-400' : isWarning ? 'text-amber-400' : 'text-red-400';
    const bgColor = isPreparing ? 'bg-blue-500/[0.06] border-blue-500/15' : isSuccess ? 'bg-emerald-500/[0.06] border-emerald-500/15' : isWarning ? 'bg-amber-500/[0.06] border-amber-500/15' : 'bg-red-500/[0.06] border-red-500/15';
    const detailBg = isPreparing ? 'bg-blue-500/[0.03] border-blue-500/10' : isSuccess ? 'bg-emerald-500/[0.03] border-emerald-500/10' : isWarning ? 'bg-amber-500/[0.03] border-amber-500/10' : 'bg-red-500/[0.03] border-red-500/10';
    const detailText = isPreparing ? 'text-blue-300/70' : isSuccess ? 'text-emerald-300/70' : isWarning ? 'text-amber-300/70' : 'text-red-300/70';

    return (
      <div className="w-full my-1" data-testid="message-function-result">
        <button onClick={() => setExpanded(!expanded)}
          data-testid={`function-result-toggle-${meta.sms_attempt_id || `${funcName}-${msg.timestamp}`}`}
          aria-expanded={expanded}
          className={`w-full flex flex-wrap items-center gap-2 px-3 py-1.5 rounded-lg border transition-all hover:brightness-110 cursor-pointer ${bgColor}`}>
          <Icon size={11} className={statusColor} />
          <span className={`text-[10px] font-medium ${statusColor}`}>{funcName === 'post_call_sms' ? 'Post-call SMS' : funcName === 'send_sms' ? 'Send SMS' : funcName}</span>
          <span className={`text-[10px] font-semibold ${statusColor}`} data-testid={details.sms_status ? `inbox-sms-status-${meta.sms_attempt_id || `${funcName}-${msg.timestamp}`}` : undefined}>{statusLabel}</span>
          {meta.time && <span className="text-emerald-300/70 text-[10px] ml-1">{meta.time}</span>}
          <ChevronDown size={10} className={`ml-auto ${statusColor} opacity-50 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          <span className={`text-[9px] ${statusColor} opacity-40`}>{ts}</span>
        </button>
        <AnimatePresence>
          {expanded && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              className={`mt-1 ml-2 px-3 py-2 rounded-lg border ${detailBg}`}>
              {details.sms_status && <div className="space-y-1 mb-2" data-testid={`inbox-sms-details-${meta.sms_attempt_id || `${funcName}-${msg.timestamp}`}`}>
                {details.prompt_name && <p className={`text-xs ${detailText}`} data-testid={`inbox-sms-prompt-${meta.sms_attempt_id}`}>Prompt: {details.prompt_name}</p>}
                <p className={`text-xs leading-relaxed break-words ${detailText}`}>{details.reason || msg.text}</p>
                {details.message_id && <p className={`text-[10px] break-all ${detailText}`}>GHL message: {details.message_id}</p>}
                {details.recipient && <p className={`text-[10px] ${detailText}`}>To {details.recipient}</p>}
              </div>}
              {/* Calendar metadata for check_availability & book_appointment */}
              {(funcName === 'check_availability' || funcName === 'book_appointment') && (details.calendar_id || details.timezone) && (
                <div className="space-y-1 mb-2">
                  {details.calendar_id && (
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[9px] font-medium ${detailText}`}>Calendar:</span>
                      <span className={`text-[10px] font-mono ${detailText}`}>{details.calendar_id}</span>
                    </div>
                  )}
                  {details.timezone && (
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[9px] font-medium ${detailText}`}>Timezone:</span>
                      <span className={`text-[10px] ${detailText}`}>{details.timezone}</span>
                    </div>
                  )}
                  {details.contact_name && (
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[9px] font-medium ${detailText}`}>Contact:</span>
                      <span className={`text-[10px] ${detailText}`}>{details.contact_name}</span>
                    </div>
                  )}
                  {details.appointment_id && (
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[9px] font-medium ${detailText}`}>Appointment ID:</span>
                      <span className={`text-[10px] font-mono ${detailText}`}>{details.appointment_id}</span>
                    </div>
                  )}
                  {details.start && (
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[9px] font-medium ${detailText}`}>Start:</span>
                      <span className={`text-[10px] ${detailText}`}>{details.start}</span>
                    </div>
                  )}
                </div>
              )}
              {/* Available slots with detailed data */}
              {meta.slots && (
                <div className="space-y-1">
                  {meta.slots.map((slot, i) => (
                    <div key={i} className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-emerald-400/[0.06]">
                      <Calendar size={9} className="text-emerald-400" />
                      <span className="text-[10px] text-emerald-300">{slot}</span>
                    </div>
                  ))}
                </div>
              )}
              {isSuccess && funcName === 'book_appointment' && (
                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-emerald-400/[0.06]">
                  <CheckCircle size={9} className="text-emerald-400" />
                  <span className="text-[10px] text-emerald-300">Confirmed{meta.time ? `: ${meta.time}` : ''}</span>
                </div>
              )}
              {/* Detailed trigger_outbound_call data */}
              {funcName === 'trigger_outbound_call' && (
                <div className="space-y-1">
                  {msg.text && <p className={`text-[10px] ${detailText} mb-1`}>{msg.text}</p>}
                  {details.to && <div className="flex items-center gap-1.5"><span className={`text-[9px] font-medium ${detailText}`}>To:</span><span className={`text-[10px] ${detailText}`}>{details.to}</span></div>}
                  {details.from && details.from !== 'unknown' && <div className="flex items-center gap-1.5"><span className={`text-[9px] font-medium ${detailText}`}>From:</span><span className={`text-[10px] ${detailText}`}>{details.from}</span></div>}
                  {details.twilio_sid && <div className="flex items-center gap-1.5"><span className={`text-[9px] font-medium ${detailText}`}>SID:</span><span className={`text-[10px] font-mono ${detailText}`}>{details.twilio_sid}</span></div>}
                  {details.voice_agent && <div className="flex items-center gap-1.5"><span className={`text-[9px] font-medium ${detailText}`}>Agent:</span><span className={`text-[10px] ${detailText}`}>{details.voice_agent}</span></div>}
                  {details.call_id && <div className="flex items-center gap-1.5"><span className={`text-[9px] font-medium ${detailText}`}>Call ID:</span><span className={`text-[10px] font-mono ${detailText}`}>{details.call_id}</span></div>}
                  {details.error && <div className="flex items-center gap-1.5"><span className={`text-[9px] font-medium ${detailText}`}>Error:</span><span className={`text-[10px] ${detailText}`}>{details.error}</span></div>}
                  {details.raw_error && details.raw_error !== details.error && <div className="flex items-center gap-1.5"><span className={`text-[9px] font-medium ${detailText}`}>Raw:</span><span className={`text-[10px] font-mono ${detailText}`}>{details.raw_error}</span></div>}
                  {details.missing_requirements && <div className="space-y-0.5">{details.missing_requirements.map((r, i) => <div key={i} className={`text-[10px] ${detailText}`}>- {r}</div>)}</div>}
                </div>
              )}
              {/* Failed reason */}
              {meta.reason && (
                <p className={`text-[10px] ${detailText} mt-1`}>{meta.reason}</p>
              )}
              {/* Generic text fallback for other functions */}
              {funcName !== 'trigger_outbound_call' && funcName !== 'check_availability' && funcName !== 'book_appointment' && !meta.slots && (
                <p className={`text-[10px] ${detailText}`}>{msg.text}</p>
              )}
              {/* Show text for check/book only when no structured details */}
              {(funcName === 'check_availability' || funcName === 'book_appointment') && !details.calendar_id && !meta.slots && !(isSuccess && funcName === 'book_appointment') && msg.text && (
                <p className={`text-[10px] ${detailText}`}>{msg.text}</p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // LeadConnector Automation / manual CRM sends — right side, amber for
  // automations, sky for messages a human sent from the CRM conversation view
  if (isAutomation) {
    const isManualCrm = meta.manual === true;
    return (
      <div className="flex justify-end" data-testid={isManualCrm ? 'message-crm-manual' : 'message-automation'}>
        <div className="max-w-[70%]">
          <div className="flex items-center justify-end gap-1.5 mb-0.5">
            {isManualCrm
              ? <User size={8} className="text-sky-400" />
              : <Zap size={8} className="text-amber-400" />}
            <span className={`text-[9px] font-medium ${isManualCrm ? 'text-sky-400' : 'text-amber-400'}`}>
              {isManualCrm ? 'Sent from CRM' : 'LeadConnector Automation'}
            </span>
            <span className={`text-[9px] ${textSecondary}`}>{ts}</span>
          </div>
          <div className={`px-3 py-2 rounded-2xl rounded-tr-sm ${
            isManualCrm
              ? (isDarkMode ? 'bg-sky-500/[0.12] border border-sky-400/20' : 'bg-sky-50 border border-sky-200')
              : (isDarkMode ? 'bg-amber-500/[0.12] border border-amber-400/20' : 'bg-amber-50 border border-amber-200')
          }`}>
            <p className={`text-[12px] ${
              isManualCrm
                ? (isDarkMode ? 'text-sky-100' : 'text-sky-800')
                : (isDarkMode ? 'text-amber-100' : 'text-amber-800')
            } whitespace-pre-wrap leading-relaxed`}>{msg.text}</p>
          </div>
        </div>
      </div>
    );
  }

  // Follow-Up AI — right side, purple for distinction
  if (isFollowUp) {
    return (
      <div className="flex justify-end" data-testid="message-followup-ai">
        <div className="max-w-[70%]">
          <div className="flex items-center justify-end gap-1.5 mb-0.5">
            <RefreshCw size={8} className="text-violet-400" />
            <span className="text-[9px] text-violet-400 font-medium">Follow-Up AI</span>
            {msg.trait && <span className={`text-[8px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-violet-500/15 text-violet-300' : 'bg-violet-100 text-violet-600'}`}>{msg.trait}</span>}
            <span className={`text-[9px] ${textSecondary}`}>{ts}</span>
          </div>
          <div className={`px-3 py-2 rounded-2xl rounded-tr-sm ${isDarkMode ? 'bg-violet-500/[0.12] border border-violet-400/20' : 'bg-violet-50 border border-violet-200'}`}>
            <p className={`text-[12px] ${isDarkMode ? 'text-violet-100' : 'text-violet-800'} whitespace-pre-wrap leading-relaxed`}>{msg.text}</p>
          </div>
        </div>
      </div>
    );
  }

  // AI Textback — right side, teal, sent by the textback executor at the
  // contact's requested time
  if (isAgent && meta.source === 'ai_textback') {
    return (
      <div className="flex justify-end" data-testid="message-ai-textback">
        <div className="max-w-[70%]">
          <div className="flex items-center justify-end gap-1.5 mb-0.5">
            <Bot size={8} className="text-teal-400" />
            <span className="text-[9px] text-teal-400 font-medium">AI - Textback</span>
            <span className={`text-[9px] ${textSecondary}`}>{ts}</span>
          </div>
          <div className={`px-3 py-2 rounded-2xl rounded-tr-sm ${isDarkMode ? 'bg-teal-500/[0.12] border border-teal-400/20' : 'bg-teal-50 border border-teal-200'}`}>
            <p className={`text-[12px] ${isDarkMode ? 'text-teal-50' : 'text-teal-800'} whitespace-pre-wrap leading-relaxed`}>{msg.text}</p>
          </div>
        </div>
      </div>
    );
  }

  // AI Agent — right side, cyan/blue for clear distinction
  if (isAgent) {
    return (
      <div className="flex justify-end" data-testid={meta.outbound_attempt_id ? `message-ai-${meta.outbound_attempt_id}` : `message-ai-${encodeURIComponent(msg.message_id || msg.timestamp || '')}`}>
        <div className="min-w-0 max-w-[85%] sm:max-w-[70%] [overflow-wrap:anywhere]">
          <div className="flex items-center justify-end gap-1.5 mb-0.5">
            <Bot size={8} className="text-cyan-400" />
            <span className="text-[9px] text-cyan-400 font-medium">AI</span>
            <span className={`text-[9px] ${textSecondary}`}>{ts}</span>
          </div>
          <div className={`px-3 py-2 rounded-2xl rounded-tr-sm ${isDarkMode ? 'bg-cyan-500/[0.12] border border-cyan-400/15' : 'bg-cyan-50 border border-cyan-200'}`}>
            <p className={`text-[12px] ${isDarkMode ? 'text-cyan-50' : 'text-cyan-800'} whitespace-pre-wrap leading-relaxed`}>{msg.text}</p>
          </div>
          <ChatDeliveryStatus meta={meta} />
        </div>
      </div>
    );
  }

  // Customer — left side
  return (
    <div className="flex justify-start" data-testid="message-customer">
      <div className="max-w-[70%]">
        <div className="flex items-center gap-1.5 mb-0.5">
          <User size={8} className={textSecondary} />
          <span className={`text-[9px] ${textSecondary} font-medium`}>SMS Customer</span>
          <span className={`text-[9px] ${textSecondary}`}>{ts}</span>
        </div>
        <div className={`px-3 py-2 rounded-2xl rounded-tl-sm ${isDarkMode ? 'bg-white/[0.04] border border-white/[0.06]' : 'bg-gray-100 border border-gray-200'}`}>
          <p className={`text-[12px] ${isDarkMode ? 'text-gray-200' : 'text-gray-800'} whitespace-pre-wrap leading-relaxed`}>{msg.text}</p>
        </div>
      </div>
    </div>
  );
};


// ── Captured by AI panel ─────────────────────────────────────────────
// Live timeline of every tool/function the AI fired in this conversation:
// tags added/removed, fields captured, contact updates, bookings, workflows,
// SMS, outbound calls — derived from the session's function_result messages,
// so WebSocket pushes keep it real-time with zero extra API calls.
const FUNC_LABELS = {
  add_tag: 'Tag added', remove_tag: 'Tag removed', update_custom_field: 'Field captured',
  update_contact: 'Contact updated', create_contact: 'Contact created',
  book_appointment: 'AI Chat Booked', check_availability: 'Availability checked',
  reschedule_appointment: 'Appointment rescheduled', cancel_appointment: 'Appointment cancelled',
  trigger_workflow: 'Workflow triggered', send_sms: 'Send SMS', post_call_sms: 'Post-call SMS',
  trigger_outbound_call: 'Outbound call', add_note: 'Note added',
  schedule_callback: 'Callback scheduled', update_opportunity_stage: 'Pipeline stage updated',
  schedule_textback: 'Textback scheduled',
  switch_to_sms: 'Switched to SMS', end_call: 'Call ended', transfer_call: 'Call transferred',
  ai_reply: 'AI Reply Fallback', ai_error: 'AI Reply Failed',
};
const FUNC_ICONS = {
  add_tag: Tag, remove_tag: Tag, update_custom_field: Database,
  update_contact: User, create_contact: User,
  book_appointment: CheckCircle, check_availability: Calendar,
  reschedule_appointment: Calendar, cancel_appointment: XCircle,
  trigger_workflow: Zap, send_sms: MessageSquare,
  trigger_outbound_call: Phone, schedule_callback: Phone, schedule_textback: MessageSquare,
  add_note: MessageSquare, switch_to_sms: MessageSquare, transfer_call: Phone,
  call_summary_note: FileText,
};

const activityStatus = (meta) => {
  if (meta.type === 'error') return 'failed';
  const s = meta.status;
  if (s === 'success' || s === 'completed') return 'success';
  if (s === 'warning') return 'warning';
  if (['preparing', 'slots_found', 'queued', 'initiated', 'ringing', 'in-progress', 'warming'].includes(s)) return 'preparing';
  return 'failed';
};

const AIActivityItem = ({ ev, i, isDarkMode, textPrimary, textSecondary, cardBorder }) => {
  const meta = ev.meta || {};
  const isVoiceCall = meta.type === 'voice_call';
  const funcName = isVoiceCall ? 'voice_call' : (meta.function || (meta.type === 'error' ? 'ai_error' : 'action'));
  const st = isVoiceCall ? ((meta.booked || meta.answered) ? 'success' : 'failed') : activityStatus(meta);
  const Icon = isVoiceCall
    ? ((meta.direction || '') === 'inbound' ? PhoneIncoming : PhoneOutgoing)
    : (FUNC_ICONS[funcName] || (funcName === 'ai_error' ? AlertTriangle : Zap));
  const label = isVoiceCall
    ? (meta.booked ? 'AI Voice Booked' : meta.answered ? 'Call Answered' : 'Call Not Answered')
    : funcName === 'call_summary_note' ? (st === 'success' ? 'Note posted' : st === 'preparing' ? 'Posting note' : 'Note failed')
    : (FUNC_LABELS[funcName] || funcName.replace(/_/g, ' '));
  const color = st === 'success' ? 'text-emerald-400' : st === 'warning' ? 'text-amber-400' : st === 'preparing' ? 'text-blue-400' : 'text-red-400';
  const ringBg = st === 'success' ? 'bg-emerald-400/10' : st === 'warning' ? 'bg-amber-400/10' : st === 'preparing' ? 'bg-blue-400/10' : 'bg-red-400/10';
  const details = meta.details || {};
  const ts = ev.timestamp ? new Date(ev.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
  const tags = Array.isArray(details.tags) ? details.tags : [];
  const fields = Array.isArray(details.fields) ? details.fields : [];

  return (
    <div className={`p-2.5 rounded-xl border ${cardBorder} ${isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50'}`} data-testid={`ai-activity-item-${i}`}>
      <div className="flex items-center gap-2">
        <div className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${ringBg}`}>
          <Icon size={12} className={color} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1">
            <span className={`text-[11px] font-semibold ${textPrimary} truncate`}>{label}</span>
            <span className={`text-[9px] font-bold uppercase tracking-wide ${color} shrink-0`}>
              {isVoiceCall
                ? (meta.duration > 0 ? `${fmtCallDur(meta.duration)} min` : (meta.answered ? 'answered' : 'missed'))
                : details.sms_status ? smsStatusLabel(details.sms_status) : st === 'preparing' ? 'IN PROGRESS' : st}
            </span>
          </div>
          <p className={`text-[9px] ${textSecondary}`}>{ts}</p>
        </div>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`ai-activity-tags-${i}`}>
          {tags.map((t, j) => (
            <span key={j} className={`text-[9px] px-1.5 py-0.5 rounded-md border font-medium ${
              funcName === 'remove_tag'
                ? 'bg-rose-400/10 text-rose-300 border-rose-400/20'
                : 'bg-teal-400/10 text-teal-300 border-teal-400/20'
            }`}>{t}</span>
          ))}
        </div>
      )}
      {fields.length > 0 && (
        <div className="mt-2 space-y-1.5" data-testid={`ai-activity-fields-${i}`}>
          {fields.map((f, j) => {
            const [k, ...rest] = String(f).split('=');
            return (
              <div key={j}>
                <p className={`text-[9px] uppercase tracking-wide font-medium ${textSecondary} mb-0.5`}>{k.trim()}</p>
                <p className={`text-[11px] ${textPrimary} font-medium break-words px-2 py-1 rounded-md border ${
                  isDarkMode ? 'bg-white/[0.04] border-white/10' : 'bg-white border-gray-200'
                }`}>{rest.join('=').trim()}</p>
              </div>
            );
          })}
        </div>
      )}
      {funcName === 'book_appointment' && st === 'success' && (meta.time || details.start) && (
        <p className="text-[10px] text-emerald-300 mt-1.5">{meta.time || details.start}</p>
      )}
      {(st === 'failed' || ['unknown', 'skipped'].includes(details.sms_status)) && (meta.reason || ev.text) && (
        <p className="text-[9px] text-red-300/80 mt-1.5 break-words">{meta.reason || ev.text}</p>
      )}
      {st !== 'failed' && !details.sms_status && tags.length === 0 && fields.length === 0 && !(funcName === 'book_appointment' && (meta.time || details.start)) && ev.text && meta.type === 'function_result' && (
        <p className={`text-[9px] ${textSecondary} mt-1.5 break-words`}>{ev.text}</p>
      )}
    </div>
  );
};

const AIActivityPanel = ({ messages, isDarkMode, cardBg, cardBorder, textPrimary, textSecondary, onClose }) => {
  const events = (messages || [])
    .filter(m => (m.role === 'system' && ((m.meta || {}).type === 'function_result' || (m.meta || {}).type === 'error'))
      || (m.role === 'call' && (m.meta || {}).type === 'voice_call'))
    .slice()
    .reverse();
  const okCount = events.filter(e => activityStatus(e.meta || {}) === 'success').length;
  const failCount = events.filter(e => activityStatus(e.meta || {}) === 'failed').length;

  return (
    <div className={`w-[300px] shrink-0 ${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} flex-col min-h-0 hidden lg:flex`} data-testid="ai-activity-panel">
      <div className={`p-3.5 border-b ${cardBorder} shrink-0 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Sparkles className="text-violet-400" size={14} />
          <h2 className={`text-sm font-semibold ${textPrimary}`}>Captured by AI</h2>
        </div>
        <button onClick={onClose} className={`p-1 rounded-md ${textSecondary} hover:bg-white/10 transition-colors`} data-testid="ai-activity-close">
          <X size={13} />
        </button>
      </div>
      {events.length > 0 && (
        <div className={`px-3.5 py-2 border-b ${cardBorder} shrink-0`} data-testid="ai-activity-summary">
          <p className={`text-[10px] ${textSecondary}`}>
            <span className="text-emerald-400 font-semibold">{okCount} succeeded</span>
            {failCount > 0 && <> · <span className="text-red-400 font-semibold">{failCount} failed</span></>}
            {' '}· {events.length} action{events.length === 1 ? '' : 's'} total
          </p>
        </div>
      )}
      <div className="flex-1 overflow-y-auto min-h-0 p-3 space-y-2">
        {events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-3 text-center" data-testid="ai-activity-empty">
            <Sparkles className="text-violet-400/40" size={20} />
            <p className={`text-xs ${textSecondary} mt-2 font-medium`}>No AI actions yet</p>
            <p className={`text-[10px] ${textSecondary} mt-1 opacity-70`}>
              Tags, captured fields, bookings and every other tool the AI fires in this conversation will appear here.
            </p>
          </div>
        ) : (
          events.map((ev, i) => (
            <AIActivityItem key={conversationMessageKey(ev)} ev={ev} i={i} isDarkMode={isDarkMode}
              textPrimary={textPrimary} textSecondary={textSecondary} cardBorder={cardBorder} />
          ))
        )}
      </div>
    </div>
  );
};


const ResumeAIButton = ({ api, subAccountId, sessionId, onResumed }) => {
  const [busy, setBusy] = useState(false);
  const resume = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/sub-accounts/${subAccountId}/conversations/${sessionId}/resume-ai`);
      toast.success(`AI resumed — tag "${data.tag}" added in your CRM`);
      if (data.warning) toast.warning(data.warning);
      onResumed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resume AI');
    } finally { setBusy(false); }
  };
  return (
    <button onClick={resume} disabled={busy} data-testid="resume-ai-btn"
      className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-teal-500 text-white text-[10px] font-semibold hover:bg-teal-600 transition-colors disabled:opacity-60">
      {busy ? <Loader2 size={10} className="animate-spin" /> : <Play size={10} />} Resume AI
    </button>
  );
};

const ConversationsPage = () => (
  <SubAccountLayout>
    <ConversationsPageContent />
  </SubAccountLayout>
);

export default ConversationsPage;
